'use client';

import { setCallDiagnosticUserGetter } from '~/shared/libs/call-telemetry';
import { AwsRum } from 'aws-rum-web';
import { useEffect } from 'react';

import { getClientUser } from '~/shared/helpers/client-user-helpers';
import { REGION } from '~/universal/constants';

declare global {
  interface Window {
    __pranaClubRum?: AwsRum;
  }
}

export default function RumInit() {
  useEffect(() => {
    setCallDiagnosticUserGetter(getClientUser);
    try {
      const isProd = process.env.NODE_ENV === 'production';

      if (!isProd) {
        console.info('RUM disabled in local environment');
        return;
      }

      const appMonitorId = process.env.NEXT_PUBLIC_CW_RUM_APP_MONITOR_ID_PROD;
      const identityPoolId = process.env.NEXT_PUBLIC_CW_RUM_IDENTITY_POOL_ID;
      const releaseId = process.env.NEXT_PUBLIC_APP_RELEASE_ID || 'local';

      if (!appMonitorId || !identityPoolId) {
        console.error('RUM skipped: missing config', {
          appMonitorId,
          identityPoolId,
          isProd,
        });
        return;
      }

      if (!window.__pranaClubRum) {
        window.__pranaClubRum = new AwsRum(appMonitorId, releaseId, REGION, {
          allowCookies: true,
          sessionSampleRate: 1,
          sessionEventLimit: 100,
          identityPoolId,
          debug: false,
          disableAutoPageView: true,
          enableXRay: false,
          enableW3CTraceId: false,
          useBeacon: true,
          releaseId,
          telemetries: [
            'errors',
            [
              'http',
              {
                urlsToExclude: [
                  /^https:\/\/www\.google-analytics\.com\//i,
                  /^https:\/\/www\.google\.com\//i,
                  /\?_rsc=/i,
                ],
              },
            ],
          ],
          /* allowCookies: true,
          sessionSampleRate: 0.05,
          telemetries: ['performance', 'errors', 'http'],
          sessionEventLimit: 500,
          identityPoolId,
          debug: false,
          disableAutoPageView: false,
          enableXRay: true,
          enableW3CTraceId: true,
          useBeacon: false,
          releaseId,
*/
        });


        console.info('RUM initialized', {
          appMonitorId,
          isProd,
          releaseId,
        });
      }

      const user = getClientUser();
      const appUserId = user?._id ? String(user._id) : 'ANONYMOUS';
      const appUserRole = user?.role ? String(user.role) : 'USER';

      window.__pranaClubRum.addSessionAttributes({
        appUserId,
        appUserRole,
      });
    } catch (error) {
      console.error('RUM init failed', error);
    }
  }, []);

  useEffect(() => {
    const refreshRumUserAttributes = () => {
      try {
        const rum = window.__pranaClubRum;
        if (!rum) return;
        const currentUser = getClientUser();
        rum.addSessionAttributes({
          appUserId: currentUser?._id ? String(currentUser._id) : 'ANONYMOUS',
          appUserRole: currentUser?.role ? String(currentUser.role) : 'USER',
        });
      } catch { /* Telemetry must not affect authentication or rendering. */ }
    };
    const timer = window.setInterval(refreshRumUserAttributes, 30000);
    window.addEventListener('focus', refreshRumUserAttributes);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', refreshRumUserAttributes);
    };
  }, []);

  return null;
}
